import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { pitchesApi } from "../../repositories/pitches";
import { useAppSelector } from "../../hooks/redux";
import { formatDisplayDate } from "../../DashboardPage/utils/dates";
import { Copy, Check, Search, X, ChevronDown } from "lucide-react";
import Pagination from "../../components/common/Pagination";
import { TrashIcon, EyeIcon, ChartBarIcon } from "@heroicons/react/24/outline"
import DeleteConfirmationModal from "../../components/modal/DeleteConfirmationModal"
import PageHeader from "../../components/ui/PageHeader";
import { HOME_CRUMB } from "../../components/ui/Breadcrumbs";
import DataTable, { DataTableColumn } from "../../components/ui/DataTable";
import RowActionsMenu, { RowMenuLink } from "../../components/ui/RowActionsMenu";
import ScoreRing from "../../components/ui/ScoreRing";
import Swal from "sweetalert2";
import { soundchartsEndpoints } from "../../repositories/soundcharts";
import { useDebounce } from "use-debounce";
import Button from "../../components/ui/Button";

// Update Pitch interface to allow null
interface Pitch {
  id: number;
  artistName: string;
  releaseTitle: string;
  releaseDate: string;
  userId: number;
  trackISRC: string;
  mainGenre?: string;
  configuration?: string;
  companyName?: string;
  popularity?: number | null;
}

const PastSubmissions: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [pitches, setPitches] = useState<Pitch[]>([]);
  /*
   * The page lives in the URL (?page=7), not in component state. Opening a row's
   * Analytics or Review Pitch unmounts this page, so local state was lost and the
   * user always came back to page 1. Detail pages echo this value back in their
   * back-links, which restores the exact page they left from.
   *
   * Garbage ("?page=abc", "?page=0", "?page=-2") falls back to 1 rather than
   * being sent to the API.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = Number(searchParams.get("page"));
  const currentPage =
    Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;

  const setCurrentPage = useCallback(
    (page: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          // Page 1 is the default, so keep it out of the URL for a clean link.
          if (page <= 1) next.delete("page");
          else next.set("page", String(page));
          return next;
        },
        // Paging is not a new destination - replace so Back leaves the list
        // instead of walking back through every page the user clicked.
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [copiedFields, setCopiedFields] = useState<{ [key: string]: boolean }>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 400);
  const [searchType, setSearchType] = useState<"artist" | "release" | "company">("artist");
  const [selectedPitchIds, setSelectedPitchIds] = useState<number[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteInput, setDeleteInput] = useState("");
  const [modalLoading, setModalLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  // True while the debounce is still pending or the request is in flight
  const isSearching = searchTerm !== debouncedSearchTerm || loading;
  const pageSize = 10;
  const isAdmin=user?.role === "admin";

  // Fetch popularity function - simple without loading state
  const fetchPopularityForPitch = async (pitch: Pitch) => {
    if (!pitch.trackISRC) return;

    try {
      const songResponse = await soundchartsEndpoints.getSongByISRC(pitch.trackISRC);
      const uuid = songResponse?.data?.object?.uuid;
      if (uuid) {
        const popularityResponse = await soundchartsEndpoints.getPopularityBySong(uuid, 'spotify');
        let popularityValue: number | undefined;
        let latestValue = popularityResponse?.data?.items?.[0]
            let array: number[] = []
            if (latestValue?.plots) {
              latestValue?.plots.forEach((plot: any) => {
                array.push(plot.value);
                popularityValue = Math.max(...array);

              })

            }else{
              popularityValue = latestValue?.value;
            }

        setPitches(prevPitches =>
          prevPitches.map(p =>
            p.id === pitch.id
              ? { ...p, popularity: popularityValue !== undefined ? popularityValue : null }
              : p
          )
        );
      } else {
        setPitches(prevPitches =>
          prevPitches.map(p =>
            p.id === pitch.id ? { ...p, popularity: null } : p
          )
        );
      }
    } catch (error) {
      setPitches(prevPitches =>
        prevPitches.map(p =>
          p.id === pitch.id ? { ...p, popularity: null } : p
        )
      );
    }
  };

  const fetchPastPitches = async () => {
    try {
      setLoading(true);
      const response = await pitchesApi.getAll({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearchTerm,
        searchType: searchType,
        past: true,
      });

      const fetchedPitches: Pitch[] = response.data.data || [];
      const sorted = fetchedPitches.sort(
        (a, b) =>
          new Date(b.releaseDate).getTime() -
          new Date(a.releaseDate).getTime()
      );

      setPitches(sorted);
      const total = response.data.pagination.totalItems || 0;
      const lastPage = Math.ceil(total / pageSize) || 1;
      setTotalItems(total);
      setTotalPages(lastPage);

      /*
       * The page can outrun the data - a hand-typed ?page=99, or rows deleted
       * until page 7 no longer exists. Snap back to the last real page so the
       * user sees rows instead of an empty table they can't page out of.
       */
      if (currentPage > lastPage) {
        setCurrentPage(lastPage);
      }

      // Fetch popularity for all pitches with ISRC
      sorted.forEach(pitch => {
        if (pitch.trackISRC) {
          fetchPopularityForPitch(pitch);
        }
      });
    } catch (error) {
      console.error("Failed to fetch pitches:", error);
      setPitches([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPastPitches();
    // searchType is intentionally NOT a dependency: changing the type clears the
    // term, so refetching would just re-request the unfiltered list. The next
    // keystroke fetches with the new type (fetchPastPitches reads it directly).
  }, [currentPage, debouncedSearchTerm, user]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  };
  const handleSearchTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSearchType(e.target.value as "artist" | "release" | "company");
    setCurrentPage(1); // Reset to first page on new search type
  };

  const clearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };;

  const truncateText = (text: string, maxLength: number) => {
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  const copyToClipboard = async (text: string, fieldKey: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedFields((prev) => ({ ...prev, [fieldKey]: true }));
      setTimeout(() => {
        setCopiedFields((prev) => ({ ...prev, [fieldKey]: false }));
      }, 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const handleDeleteConfirmed = async () => {
    setModalLoading(true);
    try {
      const response = await pitchesApi.deletePitch(selectedPitchIds);
      setSelectedPitchIds([]);
      setShowDeleteModal(false);
      setDeleteInput("");
      setModalLoading(false);
      fetchPastPitches();
      Swal.fire({
        title: response?.data?.success ? "Deleted!" : "Error!",
        text: response?.data?.message || "Pitch deleted successfully!",
        icon: response?.data?.success ? "success" : "error",
        confirmButtonText: "OK",
      });
    } catch (error: any) {
      setShowDeleteModal(false);
      setDeleteInput("");
      setModalLoading(false);
      Swal.fire({
        title: "Error!",
        text: error?.response?.data?.message || error.message || "Request failed",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  // Same checkbox / button / cell treatment as the tables on /dashboard.
  const checkboxClass =
    "w-4 h-4 rounded border-neutral-300 text-primary-700 focus:border-primary-700";

  const allOnPageChecked =
    pitches.length > 0 && pitches.every((p) => selectedPitchIds.includes(p.id));

  /*
   * Query string handed to detail pages so their back-link can restore this
   * exact list position. Empty on page 1, which is the default anyway - no
   * point dirtying the URL for it.
   */
  const returnQuery = currentPage > 1 ? `?page=${currentPage}` : "";

  const columns: DataTableColumn<Pitch>[] = [
    ...(isAdmin
      ? [
          {
            key: "select",
            header: (
              <input
                type="checkbox"
                checked={allOnPageChecked}
                onChange={(e) => {
                  if (e.target.checked) {
                    // Add current page IDs to selectedPitchIds (avoid duplicates)
                    setSelectedPitchIds((prev) =>
                      Array.from(new Set([...prev, ...pitches.map((p) => p.id)]))
                    );
                  } else {
                    // Remove only current page IDs from selectedPitchIds
                    setSelectedPitchIds((prev) =>
                      prev.filter((id) => !pitches.map((p) => p.id).includes(id))
                    );
                  }
                }}
                aria-label="Select all"
                className={checkboxClass}
              />
            ),
            cell: (pitch: Pitch) => (
              <input
                type="checkbox"
                checked={selectedPitchIds.includes(pitch.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedPitchIds((prev) => [...prev, pitch.id]);
                  } else {
                    setSelectedPitchIds((prev) =>
                      prev.filter((id) => id !== pitch.id)
                    );
                  }
                }}
                aria-label={`Select ${pitch.artistName} - ${pitch.releaseTitle}`}
                className={checkboxClass}
              />
            ),
          },
          {
            key: "company",
            header: "Company Name",
            cell: (pitch: Pitch) => pitch.companyName,
          },
        ]
      : []),
    {
      key: "artist",
      header: "Artist",
      cell: (pitch: Pitch) => (
        <span className="font-medium">{pitch.artistName}</span>
      ),
    },
    {
      key: "release",
      header: "Release",
      cell: (pitch: Pitch) => (
        <div
          className="max-w-xs font-medium truncate"
          title={pitch.releaseTitle}
        >
          {pitch.releaseTitle}
        </div>
      ),
    },
    {
      key: "releaseDate",
      header: "Release Date",
      cell: (pitch: Pitch) => (
        <span className="text-neutral-600">
          {formatDisplayDate(pitch.releaseDate)}
        </span>
      ),
    },
    {
      key: "popularity",
      header: "Popularity",
      // A real 0-100 score, shown as a compact ring so the column costs one
      // small square rather than a number plus a wide track.
      cell: (pitch: Pitch) =>
        pitch.popularity !== undefined && pitch.popularity !== null ? (
          <ScoreRing value={pitch.popularity} label="Popularity" />
        ) : (
          <span className="text-neutral-400">Not Available</span>
        ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "center",
      // Collapsed into the shared row-actions menu, so the column is one icon
      // wide instead of two competing buttons on every row.
      // Both links carry the page the user is on, so the detail page's back
      // link can return them to it instead of dumping them on page 1.
      cell: (pitch: Pitch) => (
        <RowActionsMenu label={`Actions for ${pitch.releaseTitle || "pitch"}`}>
          <RowMenuLink
            to={`/pitch/submission/${pitch.id}${returnQuery}`}
            icon={<EyeIcon className="w-4 h-4" />}
          >
            Review Pitch
          </RowMenuLink>
          {pitch.trackISRC && (
            <RowMenuLink
              to={`/analytics/detail/${pitch.id}${returnQuery}`}
              icon={<ChartBarIcon className="w-4 h-4" />}
            >
              Analytics
            </RowMenuLink>
          )}
        </RowActionsMenu>
      ),
    },
  ];

  return (
    <div className="dashboard-container py-2 sm:p-6">
      <PageHeader
        breadcrumbs={[HOME_CRUMB, { label: "Past Submissions" }]}
        className="mb-6"
        title="Past Submissions"
        description="View and manage your past music submissions"
      />

      {/* Search Results Info */}
      {searchTerm && (
        <div className="p-3 mb-4 border rounded-lg border-primary-200 bg-primary-50">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center min-w-0">
              <Search className="w-4 h-4 mr-2 shrink-0 text-primary-700" />
              <span className="text-sm truncate text-primary-900">
                {isSearching ? "Searching..." : `Found ${totalItems} result${pitches.length !== 1 ? 's' : ''} for "${debouncedSearchTerm}" in ${searchType === "artist"
                    ? "artist names"
                    : searchType === "release"
                      ? "release names"
                      : "company names"
                  }`}
              </span>
            </div>
          </div>
        </div>
      )}

      {/*
        Toolbar above the table, same white chrome block as /dashboard: the
        selection bar sits on the leading edge, the unified search control on
        the trailing edge.
      */}
      <div className="flex flex-col gap-4 px-4 py-3 bg-white border rounded-t-lg border-neutral-200 sm:flex-row sm:items-center">
        {selectedPitchIds.length > 0 && (
          <div className="flex items-center gap-3">
            <span className="text-sm text-neutral-600">
              {selectedPitchIds.length} selected
            </span>
            <Button
              variant="dangerOutline"
              onClick={() => setShowDeleteModal(true)}
              aria-label="Delete selected pitches"
            >
              <TrashIcon className="w-4 h-4" />
              <span>Delete Selected Pitches</span>
            </Button>
          </div>
        )}

        {/*
          Search type + term as one control: a single border, radius and
          focus-within treatment wrap both, with an inset divider between them.
        */}
        <div className="flex items-stretch w-full bg-white border rounded-lg border-neutral-300 focus-within:border-primary-500 sm:w-auto sm:ml-auto">
          {/* Search Type Selector */}
          <div className="relative shrink-0">
            <select
              value={searchType}
              onChange={handleSearchTypeChange}
              aria-label="Search past submissions by field"
              className="w-[104px] sm:w-auto h-full py-2 pl-3 text-sm truncate bg-transparent border-0 appearance-none cursor-pointer pr-7 text-neutral-600 focus:outline-none focus:ring-0"
            >
              <option value="artist">Artist Name</option>
              <option value="release">Release Name</option>
              {
                user?.role === "admin" && <option value="company">Company Name</option>
              }

            </select>
            <ChevronDown className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none right-2 top-1/2 text-neutral-400" />
          </div>

          <span aria-hidden="true" className="self-stretch w-px my-1.5 bg-neutral-200" />

          {/* Search Input */}
          <div className="relative flex-1 min-w-0 sm:w-64">
            <Search className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none left-3 top-1/2 text-neutral-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder={`Search by ${searchType === "artist" ? "artist name" : "release name"}...`}
              className="w-full py-2 text-sm text-black bg-transparent border-0 rounded-r-lg pl-9 pr-9 placeholder-neutral-400 focus:outline-none focus:ring-0"
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="border border-transparent absolute p-0.5 -translate-y-1/2 rounded right-2 top-1/2 text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <DataTable
        className="rounded-t-none"
        columns={columns}
        rows={pitches}
        loading={loading}
        getRowKey={(pitch: Pitch) => pitch.id}
        emptyMessage={
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <span className="inline-flex items-center justify-center w-12 h-12 mb-3 rounded-full bg-primary-50 text-primary-700">
              {searchTerm ? (
                <Search className="w-6 h-6" />
              ) : (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
                  />
                </svg>
              )}
            </span>
            <p className="text-sm font-medium text-black">
              {searchTerm ? "No matching submissions" : "No past submissions yet"}
            </p>
            <p className="max-w-sm mt-1 text-sm text-neutral-500">
              {searchTerm
                ? `Nothing matches "${searchTerm}" in ${searchType === "artist" ? "artist names" : "release names"}. Try a different search term.`
                : "Once your first release date passes, you'll see it here along with analytics and review options."
              }
            </p>
            {searchTerm && (
              <Button variant="secondary" onClick={clearSearch} className="mt-4">
                <X className="w-4 h-4" />
                Clear search
              </Button>
            )}
          </div>
        }
      />

      <div className="mt-3">
        <Pagination
          page={currentPage}
          totalPages={totalPages}
          setPage={setCurrentPage}
          totalItems={totalItems}
          limit={pageSize}
        />
      </div>


      {showDeleteModal && (
        <DeleteConfirmationModal
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirmed}
          inputValue={deleteInput}
          setInputValue={setDeleteInput}
          loading={modalLoading}
        />
      )}
    </div>
  );
};

export default PastSubmissions;
